<template>
    <v-dialog persistent class="dialog" v-model="dialog" origin="top top">
        <v-card class="dialog-rebet__container">
            <template v-if="phase === 'printing'">
                <v-card-title class="text-h8 dialog-rebet__title">
                    <span class="dialog-rebet__title__red dialog-rebet__title__block">{{ getTranslatedText('coupon print title') }}</span>
                </v-card-title>
                <v-card-text class="dialog-rebet__description">{{ getTranslatedText('coupon print printing body') }}</v-card-text>
                <v-card-text v-if="printCountdownSeconds > 0" class="dialog-rebet__description dialog-rebet__countdown">
                    {{ getTranslatedText('reprint dialog countdown seconds', { seconds: String(printCountdownSeconds) }) }}
                </v-card-text>
            </template>
            <template v-else-if="phase === 'failed'">
                <v-card-title class="text-h8 dialog-rebet__title">
                    <span class="dialog-rebet__title__red dialog-rebet__title__block">{{ getTranslatedText('coupon print failed headline') }}</span>
                </v-card-title>
                <v-card-text class="dialog-rebet__description">{{ getTranslatedText('coupon print failed body') }}</v-card-text>
                <v-card-actions class="dialog-rebet__actions dialog-rebet__actions--right">
                    <ButtonCommon
                        value="close"
                        class-name="dialog__box-button__item uppercase"
                        :isNoHover="true"
                        :isLoading="false"
                        nameFunction="on-coupon-print-close"
                    />
                    <ButtonCommon
                        value="reprint"
                        class-name="dialog__box-button__item uppercase"
                        :isNoHover="true"
                        :isLoading="false"
                        nameFunction="on-coupon-print-reprint"
                    />
                </v-card-actions>
            </template>
        </v-card>
    </v-dialog>
</template>

<script>
import languageMixins from '@/mixins/language/language-mixins'
import printMixin from '@/mixins/print/print-mixin'
import EventBus from '@/utils/EventBus'
import ButtonCommon from '@/components/common/button/index.vue'
import CouponService from '@/services/coupon.service'

// Isolated FreeBet-coupon print flow. Mirrors the device print handshake the
// transact path uses (print-by-format -> print-status ack) so a coupon print
// gets the same printer feedback + reprint-on-failure UX, but it deliberately
// shares NO code with the betslip reprint dialog: a coupon is already issued
// and persisted retail-side, so a failed print must never cancel anything —
// the cashier just reprints or closes and the coupon stays valid.
export default {
    mixins: [languageMixins, printMixin],
    components: { ButtonCommon },
    data() {
        return {
            dialog: false,
            phase: 'idle',
            couponData: null,
            // Anchor the validity clock once, before the first print. markPrinted is
            // first-print-wins server-side; reprints reuse the expiry already stamped.
            stamped: false,
            printCountdownSeconds: 0,
            printCountdownIntervalId: null,
            ackTimeoutId: null,
            forceFinishMs: this.$store.state.milisecondsToForceFinishPrinting ?? 4000,
        }
    },
    mounted() {
        document.addEventListener('message', this.handlePrintMessageFromApk)
        window.addEventListener('message', this.handlePrintMessageFromApk)
    },
    created() {
        EventBus.$on('on-show-coupon-print', (coupon) => this.show(coupon))
        EventBus.$on('on-coupon-print-reprint', () => this.reprint())
        EventBus.$on('on-coupon-print-close', () => this.close())
    },
    beforeDestroy() {
        this.clearTimers()
        document.removeEventListener('message', this.handlePrintMessageFromApk)
        window.removeEventListener('message', this.handlePrintMessageFromApk)
    },
    destroyed() {
        EventBus.$off('on-show-coupon-print')
        EventBus.$off('on-coupon-print-reprint')
        EventBus.$off('on-coupon-print-close')
    },
    methods: {
        show(coupon) {
            if (!coupon) return
            this.couponData = coupon
            this.stamped = false
            this.dialog = true
            this.startPrinting()
        },

        // Anchor expires_at server-side (first print wins) and merge the returned
        // expiry/print date back onto couponData BEFORE printing, so the paper shows
        // the real expiry. Best-effort: a failed stamp must not block the print — the
        // coupon stays valid (worst case the paper's expiry line is blank).
        async ensureStamped() {
            if (this.stamped) return
            const code = this.couponData && this.couponData.code ? this.couponData.code : ''
            if (!code) return
            try {
                const body = await CouponService.markPrinted(code)
                const info = body && body.data ? body.data : body
                if (info) {
                    if (info.expires_at) this.$set(this.couponData, 'expires_at', info.expires_at)
                    if (info.printed_at) this.$set(this.couponData, 'printed_at', info.printed_at)
                }
                this.stamped = true
            } catch (e) {
                // best-effort — proceed to print regardless
            }
        },

        async startPrinting() {
            this.phase = 'printing'
            this.clearTimers()

            // Stamp first so the printed coupon carries the anchored expiry.
            await this.ensureStamped()

            const ms = this.forceFinishMs > 0 ? this.forceFinishMs : 4000
            this.printCountdownSeconds = Math.ceil(ms / 1000)
            this.printCountdownIntervalId = setInterval(() => {
                const next = this.printCountdownSeconds - 1
                this.printCountdownSeconds = next > 0 ? next : 0
                if (next <= 0) this.clearCountdown()
            }, 1000)

            // If the device never acknowledges (no print-status), surface the
            // failure UI so the cashier can retry instead of staring at a spinner.
            this.ackTimeoutId = setTimeout(() => {
                this.ackTimeoutId = null
                if (this.phase === 'printing') this.enterFailed()
            }, ms + 1500)

            this.$nextTick(() => this.printFreebetCoupon(this.couponData))
        },

        handlePrintMessageFromApk(event) {
            // Only react while our own dialog is driving a print, so this never
            // interferes with the transact reprint dialog's print-status handling.
            if (!this.dialog || this.phase !== 'printing') return

            let resp
            try {
                resp = JSON.parse(event.data)
            } catch (e) {
                return
            }
            if (!resp || resp.command !== 'print-status') return

            // code 100 = "started" heartbeat — the real 200/400 is still coming.
            if (resp.code === 100) return

            // "Printer is busying" — the previous job is still running; its own
            // print-status will arrive next.
            const isBusy = resp.code === 400 && !resp.errorCode &&
                typeof resp.message === 'string' && resp.message.toLowerCase().includes('busy')
            if (isBusy) return

            if (resp.code === 200) {
                this.onSuccess()
            } else {
                this.enterFailed()
            }
        },

        async onSuccess() {
            this.clearTimers()
            // printed_at / expires_at were already stamped before printing
            // (ensureStamped), so success just notifies and closes.
            const code = this.couponData && this.couponData.code ? this.couponData.code : ''
            EventBus.$emit('on-coupon-printed', { code })
            this.showNotification(this.getTranslatedText('coupon print success'), 'success')
            this.close()
        },

        enterFailed() {
            this.clearTimers()
            this.phase = 'failed'
        },

        reprint() {
            this.startPrinting()
        },

        close() {
            this.clearTimers()
            this.dialog = false
            this.phase = 'idle'
            this.couponData = null
            this.stamped = false
        },

        clearCountdown() {
            if (this.printCountdownIntervalId != null) {
                clearInterval(this.printCountdownIntervalId)
                this.printCountdownIntervalId = null
            }
        },

        clearTimers() {
            this.clearCountdown()
            if (this.ackTimeoutId != null) {
                clearTimeout(this.ackTimeoutId)
                this.ackTimeoutId = null
            }
        },

        showNotification(text, type) {
            EventBus.$emit('show-notications', { text, type })
        },
    },
}
</script>

<style lang="scss">
@import "~ThemePath/scss/common/_dialog-rebet.scss";

.dialog-rebet__countdown {
    font-weight: 600;
    margin-top: 8px;
}

.dialog-rebet__title__block {
    display: block;
    width: 100%;
}

.dialog-rebet__actions {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center;
    width: 100%;
    gap: 12px;
}

.dialog-rebet__actions--right {
    justify-content: flex-end !important;
}
</style>
